import { BASE_URL, buildProviderUser } from "../authUtils";

const JSON_HEADERS = { "Content-Type": "application/json" };

class HttpResponseError extends Error {}

async function postJson(path, body) {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: JSON_HEADERS,
    credentials: "include",
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) throw new HttpResponseError(`Request failed: ${path}`);
  return await response.json();
}

export async function postLogout(path) {
  await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    credentials: "include",
  }).catch(() => {});
}

async function requestAccessToken(path, body) {
  const payload = await postJson(path, body);
  const accessToken = payload?.accessToken;
  if (!accessToken) throw new Error(`Missing access token: ${path}`);
  return accessToken;
}

async function requestAccessTokenWithCompletionFallback(
  path,
  completionTokenKey,
  completionToken,
) {
  const body = completionToken
    ? { [completionTokenKey]: completionToken }
    : undefined;

  try {
    return await requestAccessToken(path, body);
  } catch (error) {
    if (!completionToken || !(error instanceof HttpResponseError)) {
      throw error;
    }
    return await requestAccessToken(path);
  }
}

export async function requestVippsAccessToken() {
  return await requestAccessToken("/vipps/get-access-token");
}

export async function requestFeideAccessToken() {
  return await requestAccessToken("/feide/get-access-token");
}

export async function requestAdminAccessToken() {
  return await requestAccessToken("/admin/get-access-token");
}

export async function requestStudentAccessToken() {
  return await requestAccessToken("/student/get-access-token");
}

export async function completeVippsSession({
  user: vippsUser,
  idToken,
  completionToken,
} = {}) {
  if (!completionToken && idToken) {
    await postJson("/vipps/get-refresh-token", { vippsIdToken: idToken });
  }

  const accessToken = await requestAccessTokenWithCompletionFallback(
    "/vipps/get-access-token",
    "vippsLoginCompleteToken",
    completionToken,
  );
  const user = buildProviderUser("vipps", accessToken, vippsUser);
  if (!user?.sub) throw new Error("Vipps access token missing subject");
  return { accessToken, user };
}

export async function completeFeideSession({
  user: feideUser,
  idToken,
  completionToken,
} = {}) {
  if (!completionToken && idToken) {
    await postJson("/feide/get-refresh-token", { feideIdToken: idToken });
  }

  const accessToken = await requestAccessTokenWithCompletionFallback(
    "/feide/get-access-token",
    "feideLoginCompleteToken",
    completionToken,
  );
  const user = buildProviderUser("feide", accessToken, feideUser);
  if (!user?.sub) throw new Error("Feide access token missing subject");
  return { accessToken, user };
}

export async function loginAdminSession({ email, secret }) {
  const payload = await postJson("/admin/login", { email, secret });
  const user = payload?.user;
  if (!user?.sub) throw new Error("Admin login missing user data");
  return user;
}

export async function loginStudentSession({ username, secret }) {
  const payload = await postJson("/student/login", { username, secret });
  const user = payload?.user;
  if (!user?.sub) throw new Error("Student login missing user data");
  return user;
}
